// components/personalize/AIBookMobileGlobal.tsx
import { pt2px } from '@/utils/converter'
import React, { useMemo } from 'react'
import Image from 'next/image'
import 'yet-another-react-lightbox/styles.css'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { LockIcon } from '../Icons'

const formatImageUrl = (url: string | undefined) => {
  if (!url) return '/placeholder.png'
  if (url.startsWith('http')) return url
  if (url.startsWith('uploads/')) {
    const cleanPath = url.replace(/^\/+/, '')
    const cdnUrl = process.env.NEXT_PUBLIC_CDN_URL?.replace(/\/+$/, '') || ''
    return `${cdnUrl}/${cleanPath}`
  }
  const cleanPath = url.replace(/^\/+/, '')
  const cdnUrl = process.env.NEXT_PUBLIC_CDN_URL?.replace(/\/+$/, '') || ''
  return `${cdnUrl}/${cleanPath}`
}

const useIsLargeDevice = (breakpoint: number) => {
  const [isLarge, setIsLarge] = React.useState(false)
  React.useEffect(() => {
    const checkSize = () => setIsLarge(window.innerWidth >= breakpoint)
    checkSize()
    window.addEventListener('resize', checkSize)
    return () => window.removeEventListener('resize', checkSize)
  }, [breakpoint])
  return isLarge
}

interface Props {
  pages: any
  pages_predefined_texts: any
  pages_dedication_texts: any[]
  product: any
  inputDedication: any
  cover_pages_predefined_texts: any
  cover_pages: any
  setStep?: any
  setActiveOption?: any
  setDedicationModal?: any
  name?: any
  dedicationImage?: any
  imageSelected?: any
  bookView?: string
  isProductRtlInBook?: any
  isInIsrael?: boolean // kept for compatibility, not used for flipping
  visiblePageCount?: number
  isAIStory?: boolean
  isAIGenerating?: boolean
  aiLoadingStep?: number
  dedicationImageMode?: any
  aiStoryImage?: any
  isEdit?: boolean
  mirrorTextForRtl?: boolean // mirror X coordinates for RTL locales
}

export default function AIBookMobileGlobal({
  pages,
  pages_predefined_texts,
  product,
  pages_dedication_texts,
  inputDedication,
  cover_pages,
  cover_pages_predefined_texts,
  setStep,
  setActiveOption,
  setDedicationModal,
  name,
  dedicationImage,
  imageSelected,
  bookView = 'single',
  isProductRtlInBook,
  isInIsrael, // not used
  visiblePageCount = Infinity,
  isAIStory,
  isAIGenerating,
  aiLoadingStep = 0,
  dedicationImageMode,
  aiStoryImage,
  isEdit = false,
  mirrorTextForRtl = false,
}: Props) {
  const { t } = useTranslation('personalizeModal')

  const aiLoadingSteps = useMemo(
    () => [
      t('aiGeneratingStory'),
      t('aiMakingHero'),
      t('aiAddingCharacters'),
      t('aiFinalizingIllustrations'),
    ],
    [t]
  )

  const isLarge = useIsLargeDevice(1024)
  const [imageWidth, setImageWidth] = React.useState(380)
  const firstCoverPageIndex = 0
  const lastCoverPageIndex = cover_pages && cover_pages.length > 1 ? 1 : 0
  const [allImagesLoaded, setAllImagesLoaded] = React.useState(false)
  const { locale } = useRouter()
  const [currentStepIndex, setCurrentStepIndex] = React.useState(0)

  const router = useRouter()
  const isNotebookRoute = router.pathname.includes('notebook')

  const handleImageLoad = (event: any) => {
    const width = event.target.clientWidth
    if (width > 0) setImageWidth(width)
  }

  const [page, setPage] = React.useState(0)
  const [scrollPosition, setScrollPosition] = React.useState(0)
    type SpreadPage = {
    image: any
    pageIndex: number
    }

    const pageSpreads: SpreadPage[] = []

    if (pages) {
    for (let i = pages.length - 1; i >= 0; i -= 2) {
        if (pages[i]) {
        pageSpreads.push({ image: pages[i], pageIndex: i })
        }
        if (pages[i - 1]) {
        pageSpreads.push({ image: pages[i - 1], pageIndex: i - 1 })
        }
    }
    }

  // ---------- MAP DISPLAYED INDEX TO ORIGINAL PAGE NUMBER (same as original) ----------
  const getTextPageNumber = (displayIndex: number): string => {
    if (isEdit) return displayIndex.toString()

    // because pageSpreads is already reversed,
    // text page number should just increase normally
    return displayIndex.toString()
    }

  const getDedicationPageIndex = () => {
    if (!dedicationImageMode || dedicationImageMode === 'no-image') return 2
    else return 1
  }

  const shouldShowDedicationImage = () => {
    if (!dedicationImageMode || dedicationImageMode === 'no-image') return false
    if (dedicationImageMode === 'ai-image' && aiStoryImage) return true
    if (dedicationImageMode === 'separate-image' && dedicationImage) return true
    return false
  }

  const getDedicationImageToShow = () => {
    if (dedicationImageMode === 'ai-image') return aiStoryImage
    if (dedicationImageMode === 'separate-image') return dedicationImage
    return null
  }

  const mobile_book_width =
    (document.getElementById('page-0')?.getBoundingClientRect().width ?? 0) + 25

  const shouldBlurPage = (pageIndex: number): boolean => {
    if (pageIndex === 0 || pageIndex === pageSpreads.length + 1) return false
    if (isAIStory && visiblePageCount < Infinity) return pageIndex > (visiblePageCount - 1)
    return false
  }

  // ---------- AI GENERATION ANIMATION ----------
  React.useEffect(() => {
    if (!isAIStory || !isAIGenerating) return
    const interval = setInterval(() => {
      setCurrentStepIndex((prev) => (prev + 1) % aiLoadingSteps.length)
    }, 3000)
    return () => clearInterval(interval)
  }, [isAIStory, isAIGenerating, aiLoadingSteps])

  const renderAIGeneratingOverlay = (index: number) => {
    if (!isAIStory || !isAIGenerating) return null
    if (index > visiblePageCount - 1) return null
    return (
      <div
        className="absolute inset-0 z-[100] flex items-center justify-center bg-white/80 backdrop-blur-sm"
        dir={locale === 'ar' ? 'rtl' : 'ltr'}
      >
        <div className="p-4 max-w-xs w-full mx-4">
          <div className="text-center">
            <h2 className="text-xl font-bold text-primary mb-3">
              {t('aiStoryGenerating')}
            </h2>
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p
              className="text-primary text-md leading-6 min-h-[3rem] line-clamp-2 animate-popup"
              key={aiLoadingSteps[currentStepIndex]}
            >
              {aiLoadingSteps[currentStepIndex]}
            </p>
          </div>
        </div>
      </div>
    )
  }

  const renderAIStoryLockOverlay = (index: number) => {
    const shouldBeBlurred = shouldBlurPage(index)
    if (!shouldBeBlurred) return null
    return (
      <div
        className="absolute inset-0 z-50 flex flex-col items-center justify-center backdrop-blur-xl bg-white/40 rounded-md"
        dir={locale === 'ar' ? 'rtl' : 'ltr'}
      >
        <div className="p-6 max-w-[80%] flex justify-center items-center">
          <div
            className="flex flex-col items-center justify-center gap-3"
            dir={locale === 'ar' ? 'rtl' : 'ltr'}
          >
            <LockIcon />
            <p className="text-primary text-center font-medium lg:text-2xl mt-4">
              {t('premiumBuyText')}
            </p>
          </div>
        </div>
      </div>
    )
  }

  // ---------- PRELOAD IMAGES ----------
  React.useEffect(() => {
    if (isAIStory) {
      setAllImagesLoaded(true)
      return
    }
    const preloadImages = async () => {
      try {
        const imageUrls = []
        imageUrls.push(formatImageUrl(cover_pages[firstCoverPageIndex]))
        imageUrls.push(formatImageUrl(cover_pages[lastCoverPageIndex]))

        if (pages) {
          for (let i = 0; i < pages.length; i++) {
            imageUrls.push(formatImageUrl(pages[i]))
          }
        }

        if (shouldShowDedicationImage()) {
          const dedicationImageToShow = getDedicationImageToShow()
          if (dedicationImageToShow) imageUrls.push(dedicationImageToShow)
        }

        await Promise.all(
          imageUrls.map((url) => {
            return new Promise((resolve, reject) => {
              const img = new (window as any).Image()
              img.src = url
              img.onload = resolve
              img.onerror = reject
            })
          })
        )
        setAllImagesLoaded(true)
      } catch (error) {
        console.error('Error preloading images:', error)
        setAllImagesLoaded(true)
      }
    }
    preloadImages()
  }, [
    pages,
    cover_pages,
    dedicationImage,
    aiStoryImage,
    dedicationImageMode,
    firstCoverPageIndex,
    lastCoverPageIndex,
    isAIStory,
  ])

  React.useEffect(() => {
    if (!allImagesLoaded) return
    const handleScroll = () => {
      const mobileBookDiv = document.getElementById('mobile-book-global')
      if (mobileBookDiv) {
        let new_page = Math.floor(mobileBookDiv.scrollLeft / mobile_book_width)
        if (new_page != 0) new_page *= -1
        setPage(new_page)
        setScrollPosition(mobileBookDiv.scrollLeft)
      }
    }
    const mobileBookDiv = document.getElementById('mobile-book-global')
    if (mobileBookDiv) mobileBookDiv.addEventListener('scroll', handleScroll)
    return () => {
      if (mobileBookDiv) mobileBookDiv.removeEventListener('scroll', handleScroll)
    }
  }, [mobile_book_width, allImagesLoaded])

  if (!isAIStory && !allImagesLoaded) {
    return (
      <div
        className="flex-1 overflow-y-auto px-4 py-6 bg-[#FFFCEB] flex items-center justify-center gap-4 min-h-[75vh]"
        dir={locale === 'ar' ? 'rtl' : 'ltr'}
      >
        <div className="w-6 h-6 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
        <h2 className="text-lg font-semibold leading-none">{t('bookLoadingText')}</h2>
      </div>
    )
  }

  if (pages) {
    const getTextAlignment = (text_align: string) => {
      if (text_align == 'C') return 'center'
      if (text_align == 'R') return 'right'
      if (text_align == 'L') return 'left'
      return 'left'
    }

    const getDimensions = (textDataArray: any[], pageNum: string) => {
      if (!textDataArray) return { width: 100, height: 100 }
      const pageData = textDataArray?.find((x: any) => x.page == pageNum)
      if (!pageData || !pageData.dimensions) return { width: 100, height: 100 }
      try {
        return JSON.parse(pageData.dimensions)
      } catch (e) {
        return { width: 100, height: 100 }
      }
    }

    const shouldRenderPage = (index: number): boolean => {
      if (!isAIStory) return true
      return index <= visiblePageCount - 1
    }

    // ---------- MIRROR X COORDINATES (if needed) ----------
    const getXPosition = (
      item: any,
      dimensions: any,
      containerWidth: number,
      isImage = false
    ) => {
      let left =
        item.X_coord_percent > 0
          ? item.X_coord_percent
          : (item.X_coord / (isImage ? dimensions.height : dimensions.width)) * 100

      if (mirrorTextForRtl && locale === 'ar') {
        // Mirror: newLeft = 100% - left - elementWidth%
        const elementWidthPercent = (item.max_width / dimensions.width) * 100
        left = 100 - left - elementWidthPercent
      }
      return left + '%'
    }

    return (
      <div className="w-full">
        <div
          className="mobile-book"
          id="mobile-book-global"
          key={`book-mobile-global-${bookView}-${dedicationImageMode}`}
        >
          {/* FRONT COVER */}
          <div
            className="relative w-full max-w-[360px] max-h-[360px] sm:max-w-[500px] sm:min-h-[500px] start-cover"
            id="page-0"
          >
            {!isAIGenerating &&
              cover_pages_predefined_texts
                ?.filter((x: any) => x.page == '0')[0]
                ?.predefined_texts?.map((item: any, idx: number) => {
                  let dimensions = JSON.parse(
                    cover_pages_predefined_texts.filter((x: any) => x.page == '0')[0][
                      'dimensions'
                    ]
                  )
                  return (
                    <div
                      className={`texts absolute ${
                        isNotebookRoute ? 'z-[10]' : 'z-[120]'
                      }`}
                      key={`mobile-global-text-${idx}-${bookView}`}
                      id={item.X_coord_percent}
                      style={{
                        fontSize:
                          (imageWidth * item.font_size) / pt2px(dimensions.width) < 10
                            ? 8
                            : (imageWidth * item.font_size) / pt2px(dimensions.width),
                        textAlign: getTextAlignment(item.text_align),
                        width: (imageWidth * item.max_width) / pt2px(dimensions.width),
                        top: item.Y_coord_percent + '%',
                        lineHeight: item.line_height,
                        whiteSpace: 'pre-wrap',
                        left: getXPosition(item, dimensions, imageWidth),
                        color: item.color,
                        fontFamily: item.font_face + ',sans-serif',
                      }}
                      dangerouslySetInnerHTML={{ __html: item.text }}
                    ></div>
                  )
                })}
            <div
              className={`w-[380px] max-w-[390px] h-[380px] max-h-[390px] lg:w-[500px] lg:h-[500px] ${
                isNotebookRoute ? 'z-[5]' : 'z-[110]'
              }`}
            >
              <Image
                alt="cover"
                className="object-cover border-[1.5px] border-primary rounded-md p-1"
                fill
                priority={!isAIStory}
                onLoad={handleImageLoad}
                src={formatImageUrl(cover_pages[firstCoverPageIndex])}
                key={'cover page'}
                unoptimized={isAIStory ? true : false}
              />
            </div>
            {renderAIGeneratingOverlay(0)}
          </div>

          {/* INSIDE PAGES - skip first element (index 0) to match original order */}
          {pageSpreads?.map((pageImage: any, index: number) => {
            // if (index === 0) return null // ⬅️ critical: skip the first element

            const shouldRenderThisPage = shouldRenderPage(index)
            const textPageNumber = pageSpreads[index].pageIndex.toString()

            // Dedication pages (index 1 and 2 after skipping)
            const isDedicationPage = pages_dedication_texts?.some(x => x.page === textPageNumber)
            if (isDedicationPage) {
              const dedicationPageToShow = getDedicationPageIndex()
            //   if (index !== dedicationPageToShow) return null

              const dedicationPageData = pages_dedication_texts?.find(
                (x: any) => x.page === textPageNumber
              )
              const hasImageBlock = dedicationPageData?.dedication_texts?.some((item: any) => !item.text) ?? false;
                const shouldShowThisPage = (shouldShowDedicationImage() && hasImageBlock) || (!shouldShowDedicationImage() && !hasImageBlock);
                if (!shouldShowThisPage) return null;
              const predefinedPageData = pages_predefined_texts?.find(
                (x: any) => x.page == textPageNumber
              )

              return (
                <div
                  className="relative w-full max-w-[360px] max-h-[360px] sm:max-w-[500px] sm:min-h-[500px]"
                  id={`page-mobile-global-${index}`}
                  key={index}
                >
                  <div className="w-[380px] max-w-[390px] h-[380px] max-h-[390px] lg:w-[500px] lg:h-[500px]">
                    <Image
                      alt="dedication page"
                      className={`block w-full h-full mob-book-image img-${index} border-[1.5px] border-primary rounded-md p-1 ${
                        !shouldRenderThisPage ? 'filter blur-sm' : ''
                      }`}
                      fill
                      priority={!isAIStory}
                      onLoad={handleImageLoad}
                      key={`dedication-bg-${index}-${dedicationImageMode}`}
                      src={formatImageUrl(pageImage.image)}
                      unoptimized={isAIStory ? true : false}
                    />
                  </div>

                  {renderAIGeneratingOverlay(index)}
                  {renderAIStoryLockOverlay(index)}

                  {/* Dedication texts (including image placeholder) */}
                  {dedicationPageData?.dedication_texts?.map(
                    (item: any, idx: number) => {
                      let dimensions = getDimensions(
                        pages_dedication_texts,
                        textPageNumber
                      )
                      if (item) {
                        const isImageBlock = !item.text
                        const top =
                          item.Y_coord_percent > 0
                            ? `${item.Y_coord_percent}%`
                            : `${(item.Y_coord / dimensions.height) * 71}%`
                        const left = getXPosition(
                          item,
                          dimensions,
                          imageWidth,
                          isImageBlock
                        )
                        const imageTop = isLarge
                          ? `${(item.Y_coord / dimensions.height) * 77.3}%`
                          : `${(item.Y_coord / dimensions.height) * 78}%`
                        const imageLeft = getXPosition(item, dimensions, imageWidth, true)
                        const width =
                          (imageWidth * item.max_width) / pt2px(dimensions.width)

                        if (isImageBlock) {
                          if (!shouldShowDedicationImage()) return null
                          const imageToShow = getDedicationImageToShow()
                          if (!imageToShow) return null
                          return (
                            <div
                              key={`image-${idx}`}
                              className="texts absolute z-[20]"
                              style={{
                                top: imageTop,
                                left: imageLeft,
                                width: width,
                                height: width,
                              }}
                            >
                              <img
                                src={imageToShow}
                                alt="Dedication Image"
                                className="object-cover rounded-full w-full h-full"
                              />
                            </div>
                          )
                        }

                        const maxWidth =
                          item.max_width && item.max_width > 0
                            ? `${item.max_width}px`
                            : `${item.width_percent}%`
                        return (
                          <div
                            key={`mobile-global-text-${idx}-${bookView}`}
                            className="texts absolute z-[20]"
                            style={{
                              fontSize:
                                (imageWidth * item.font_size) /
                                  pt2px(dimensions.width) <
                                10
                                  ? 8
                                  : (imageWidth * item.font_size) /
                                    pt2px(dimensions.width),
                              textAlign: getTextAlignment(item.text_align),
                              width:
                                (imageWidth * item.max_width) /
                                pt2px(dimensions.width),
                              top: top,
                              lineHeight: item.line_height,
                              whiteSpace: 'pre-wrap',
                              left: left,
                              color: item.color,
                              fontFamily: item.font_face + ',sans-serif',
                              maxWidth: maxWidth,
                            }}
                          >
                            {item.text === 'أدخل النص'
                              ? inputDedication.replace(
                                  '{basmti}',
                                  name ??
                                    (locale === 'ar'
                                      ? product?.replace_name?.ar
                                      : product?.replace_name?.en)
                                )
                              : item.text?.includes('{basmti}')
                              ? item.text.replace(
                                  '{basmti}',
                                  name ??
                                    (locale === 'ar'
                                      ? product?.replace_name?.ar
                                      : product?.replace_name?.en)
                                )
                              : item.text}
                          </div>
                        )
                      } else {
                        return null
                      }
                    }
                  )}

                  {/* Predefined texts on dedication page */}
                  {predefinedPageData?.predefined_texts?.map(
                    (item: any, idx: number) => {
                      let dimensions = getDimensions(
                        pages_predefined_texts,
                        textPageNumber
                      )
                      if (item) {
                        return (
                          <div
                            className="texts absolute"
                            key={`mobile-global-predefined-${idx}-${bookView}`}
                            style={{
                              fontSize:
                                (imageWidth * item.font_size) /
                                pt2px(dimensions.width),
                              width:
                                (imageWidth * item.max_width) /
                                pt2px(dimensions.width),
                              top: item.Y_coord_percent + '%',
                              textAlign: getTextAlignment(item.text_align),
                              lineHeight: item.line_height,
                              whiteSpace: 'pre-wrap',
                              color: item.color,
                              left: getXPosition(item, dimensions, imageWidth),
                              fontFamily: item.font_face + ',sans-serif',
                            }}
                            dangerouslySetInnerHTML={{ __html: item.text }}
                          ></div>
                        )
                      } else {
                        return null
                      }
                    }
                  )}

                  {inputDedication === '' && !isAIStory && (
                    <>
                      <div
                        className="absolute -top-6 left-1/2 -translate-x-1/2 bg-primary text-white text-center p-4 h-[52px] w-contain rounded-lg text-sm cursor-pointer lg:hidden"
                        onClick={() => {
                          setStep('edit')
                          setActiveOption('dedication')
                        }}
                      >
                        <p>{t('addDedicationText')}</p>
                      </div>
                      <div
                        className="hidden lg:block absolute top-10 left-1/2 -translate-x-1/2 bg-primary text-white text-center p-4 h-[52px] w-contain rounded-lg cursor-pointer text-sm"
                        onClick={() => {
                          setDedicationModal(true)
                        }}
                      >
                        <p>{t('addDedicationText')}</p>
                      </div>
                    </>
                  )}
                </div>
              )
            } else {
              // Regular pages
              const dedicationPageData = pages_dedication_texts?.find(
                (x: any) => x.page == textPageNumber
              )
              const predefinedPageData = pages_predefined_texts?.find(
                (x: any) => x.page == textPageNumber
              )

              return (
                <div
                  className="relative w-full max-w-[360px] max-h-[360px] sm:max-w-[500px] sm:min-h-[500px]"
                  id={`page-mobile-global-${index}`}
                  key={index}
                >
                  <div className="w-[380px] max-w-[390px] h-[380px] max-h-[390px] lg:w-[500px] lg:h-[500px]">
                    <Image
                      alt="page"
                      className={`block w-full mob-book-image img-${index} border-[1.5px] border-primary rounded-md p-1 ${
                        !shouldRenderThisPage ? 'filter blur-sm' : ''
                      }`}
                      fill
                      priority={!isAIStory}
                      onLoad={handleImageLoad}
                      key={index}
                      src={formatImageUrl(pageImage.image)}
                      unoptimized={isAIStory ? true : false}
                    />
                  </div>

                  {renderAIGeneratingOverlay(index)}
                  {renderAIStoryLockOverlay(index)}

                  {/* Dedication texts */}
                  {dedicationPageData?.dedication_texts?.map(
                    (item: any, idx: number) => {
                      let dimensions = getDimensions(
                        pages_dedication_texts,
                        textPageNumber
                      )
                      if (item) {
                        return (
                          <div
                            className={`texts absolute ${
                              index === 1 && inputDedication == '' ? 'mt-10' : ''
                            }`}
                            key={`mobile-global-dedication-${idx}-${bookView}`}
                            style={{
                              fontSize:
                                (imageWidth * item.font_size) /
                                  pt2px(dimensions.width) <
                                10
                                  ? 8
                                  : (imageWidth * item.font_size) /
                                    pt2px(dimensions.width),
                              width:
                                (imageWidth * item.max_width) /
                                pt2px(dimensions.width),
                              top: item.Y_coord_percent + '%',
                              whiteSpace: 'pre-wrap',
                              textAlign: getTextAlignment(item.text_align),
                              lineHeight: item.line_height,
                              color: item.color,
                              left: getXPosition(item, dimensions, imageWidth),
                              fontFamily: item.font_face + ',sans-serif',
                              direction: /^[a-zA-Z0-9\s.,!?;:'"\-()]*$/.test(
                                inputDedication
                              )
                                ? 'ltr'
                                : undefined,
                            }}
                          >
                            {inputDedication.replace(
                              '{basmti}',
                              name ??
                                (locale === 'ar'
                                  ? product?.replace_name?.ar
                                  : product?.replace_name?.en)
                            )}
                          </div>
                        )
                      } else {
                        return null
                      }
                    }
                  )}

                  {/* Predefined texts */}
                  {predefinedPageData?.predefined_texts?.map(
                    (item: any, idx: number) => {
                      let dimensions = getDimensions(
                        pages_predefined_texts,
                        textPageNumber
                      )
                      if (item) {
                        return (
                          <div
                            className="texts absolute"
                            key={`mobile-global-predefined-${idx}-${bookView}`}
                            style={{
                              fontSize:
                                (imageWidth * item.font_size) /
                                pt2px(dimensions.width),
                              width:
                                (imageWidth * item.max_width) /
                                pt2px(dimensions.width),
                              top: item.Y_coord_percent + '%',
                              textAlign: getTextAlignment(item.text_align),
                              lineHeight: item.line_height,
                              whiteSpace: 'pre-wrap',
                              color: item.color,
                              left: getXPosition(item, dimensions, imageWidth),
                              fontFamily: item.font_face + ',sans-serif',
                            }}
                            dangerouslySetInnerHTML={{ __html: item.text }}
                          ></div>
                        )
                      } else {
                        return null
                      }
                    }
                  )}

                  {inputDedication === '' && index === 1 && !isAIStory && (
                    <>
                      <button
                        className="bg-primary-500 py-4 px-6 absolute -top-4 right-0 left-0 mx-auto rounded-lg max-w-[150px] flex lg:hidden items-center justify-center"
                        onClick={() => {
                          setStep('edit')
                          setActiveOption('dedication')
                        }}
                      >
                        <p className="font-medium text-white text-base">اكتب اهداء</p>
                      </button>
                      <button
                        className="bg-primary-500 py-4 px-6 absolute -top-4 right-0 left-0 mx-auto rounded-lg max-w-[150px] hidden lg:flex items-center justify-center"
                        onClick={() => {
                          setDedicationModal(true)
                        }}
                      >
                        <p className="font-medium text-white text-base">اكتب اهداء</p>
                      </button>
                    </>
                  )}
                </div>
              )
            }
          })}

          {/* BACK COVER */}
          <div
            className="relative w-full max-w-[360px] max-h-[360px] sm:max-w-[500px] sm:min-h-[500px] mb-72 scroll-mb-96"
            id={`page-mobile-global-${pageSpreads.length + 1}`}
          >
            {cover_pages_predefined_texts
              ?.filter((x: any) => x.page == '1')[0]
              ?.predefined_texts?.map((item: any, idx: number) => {
                let dimensions = JSON.parse(
                  cover_pages_predefined_texts.filter((x: any) => x.page == '1')[0][
                    'dimensions'
                  ]
                )
                return (
                  <div
                    className="texts absolute"
                    key={`mobile-global-text-${idx}-${bookView}`}
                    id={item.X_coord_percent}
                    style={{
                      fontSize:
                        (imageWidth * item.font_size) / pt2px(dimensions.width) < 10
                          ? 8
                          : (imageWidth * item.font_size) / pt2px(dimensions.width),
                      textAlign: getTextAlignment(item.text_align),
                      width: (imageWidth * item.max_width) / pt2px(dimensions.width),
                      top: item.Y_coord_percent + '%',
                      lineHeight: item.line_height,
                      left: getXPosition(item, dimensions, imageWidth),
                      whiteSpace: 'pre-wrap',
                      color: item.color,
                      fontFamily: item.font_face + ',sans-serif',
                    }}
                    dangerouslySetInnerHTML={{ __html: item.text }}
                  ></div>
                )
              })}
            <div className="w-[380px] max-w-[390px] h-[380px] max-h-[390px] lg:w-[500px] lg:h-[500px]">
              <Image
                alt="cover"
                className="object-cover border-[1.5px] border-primary rounded-md p-1"
                fill
                priority={!isAIStory}
                onLoad={handleImageLoad}
                src={formatImageUrl(cover_pages[lastCoverPageIndex])}
                unoptimized
              />
            </div>
            {renderAIGeneratingOverlay(0)}
          </div>
        </div>
      </div>
    )
  }
  return <></>
}